import { BlissSVGBuilder } from "bliss-svg-builder";
import { css, html, LitElement, unsafeCSS } from "lit";

class ShapeBoard extends LitElement {
  static get properties() {
    return {
      /* bliss code, i.e. "H;C8:0,8" */
      code: {
        type: String,
      },
      _svgs: {
        type: Array,
      },
      /* px multiplier, i.e. 9 means a full size character will be 8*9=72px wide and 8*9=72px high */
      size: {
        type: Number,
      },
      svg: {
        type: String,
      },
      width: {
        type: Number,
      },
      height: {
        type: Number,
      },
      _shapeMoving: {
        type: Boolean,
      },
      _movableShapeData: {
        type: Object,
      },
    };
  }

  constructor() {
    super();
    this.code = "";
    this._svgs = [];
    this.size = 12;
    this._shapeMoving = false;
  }

  updated(changedProperties) {
    if (changedProperties.has("code")) {
      this._svgs = this.code.split(";").flatMap((c) => {
        return [this.getSvg(c, 1.5), this.getSvg(c, 0.5)];
      });
    }
  }

  async firstUpdated() {
    const handleMousedown = (event) => {
      const leftMouseButtonOnlyDown = event.buttons === undefined ? event.which === 1 : event.buttons === 1;
      if (leftMouseButtonOnlyDown) {
        if (event.target.nodeName === "path") {
          const svgElement = event.target.closest("svg");
          const svgRect = svgElement.getBoundingClientRect();
          const internalX = Number(svgElement.getAttribute("data-internal-x"));
          const internalY = Number(svgElement.getAttribute("data-internal-y"));
          this._movableShapeData = {
            width: Number(svgElement.getAttribute("data-width")),
            height: Number(svgElement.getAttribute("data-height")),
            internalX,
            internalY,
            x: svgRect.left,
            y: svgRect.top,
            code: svgElement.getAttribute("data-code"),
            pointerX: event.clientX,
            pointerY: event.clientY,
          };
          document.body.style.cursor = "grabbing";

          this._shapeMoving = true;
        }
      }
    };
    //this.shadowRoot.addEventListener('mousedown', handleMousedown);
    this.shadowRoot.addEventListener("pointerdown", handleMousedown);
  }

  handleShapeDropped(_e) {
    //console.log("shape dropped");
    document.body.style.cursor = "";
    this._shapeMoving = false;
  }

  handleShapeMoved(_e) {}

  getSvg(code, width) {
    const isWide = width > 1;

    const [shapeCode, shapePosition] = code.split(":");
    const svg = new BlissSVGBuilder(`[grid=0;stroke-width=${width};freestyle=1]||${shapeCode}`).svgElement;

    const shapePositionX = shapePosition?.includes(",") ? Number(shapePosition.split(",")[0]) : 0;
    const shapePositionY = shapePosition?.includes(",") ? Number(shapePosition.split(",")[1]) : 0;

    const [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight] = svg.getAttribute("viewBox").split(" ").map(Number);
    const gridWidth = viewBoxWidth + viewBoxX * 2; //typical grid height is 20, as if zero was used for stroke-width
    const gridHeight = viewBoxHeight + viewBoxY * 2;
    svg.style.width = `${viewBoxWidth * this.size}px`;
    svg.style.height = `${viewBoxHeight * this.size}px`;
    svg.style.left = `${(viewBoxX + shapePositionX) * this.size}px`;
    svg.style.top = `${(viewBoxY + shapePositionY) * this.size}px`;

    // Also build the normal (thin) SVG to get its viewBox for accurate internalX/Y
    const normalSvg = isWide
      ? new BlissSVGBuilder(`[grid=0;stroke-width=0.5;freestyle=1]||${shapeCode}`).svgElement
      : null;
    const [normalVbX, normalVbY] = normalSvg
      ? normalSvg.getAttribute("viewBox").split(" ").map(Number)
      : [viewBoxX, viewBoxY];

    if (isWide) {
      svg.setAttribute("data-width", gridWidth * this.size);
      svg.setAttribute("data-height", gridHeight * this.size);
      svg.setAttribute("data-internal-x", normalVbX * this.size);
      svg.setAttribute("data-internal-y", normalVbY * this.size);
      svg.setAttribute("data-code", shapeCode);
      svg.id = "wideSvg";
    } else {
      svg.id = "normalSvg";
    }
    //this.shadowRoot.appendChild(wideShapeSvg);
    //this.shadowRoot.appendChild(shapeSvg);

    //svg.setAttribute("width", 68);
    //svg.setAttribute("height", 68);
    //let viewBox = svg.getAttribute("viewBox");
    //const viewBoxValues = viewBox.split(" ");
    //viewBoxValues[1] = "-0.25";
    //viewBoxValues[3] = "8";
    //viewBox = viewBoxValues.join(" ");
    //svg.setAttribute("viewBox", viewBox);
    return svg;
  }

  static get styles() {
    return css`
      :host {
        touch-action: none; /*necessary for dragging shapes on mobile*/
        display: block;
        position: relative;
        overflow: visible;
        height: 264px;
        width: 100%;
        background: var(--bl-color-secondary-50);
        box-sizing: border-box;
        border-radius: 0 0 var(--bl-radius-md) var(--bl-radius-md);
      }

      svg {
        pointer-events: none;
        position: absolute;
      }

      svg > path,
      svg > g > path {
        pointer-events: stroke;
      }

      svg#normalSvg > path,
      svg#normalSvg > g > path {
        pointer-events: none;
      }

      svg#wideSvg {
        stroke: rgba(0, 0, 0, 0.001);
      }

      svg#wideSvg:hover {
        stroke: rgba(0, 0, 0, 0.15);
      }

      svg#wideSvg:active {
        stroke: var(--bl-color-accent-400);
        stroke: rgba(0, 0, 0, 0.001);
        cursor: grabbing !important;
      }
    `;
  }

  render() {
    return html`
      <style>
        :host {
          width: ${unsafeCSS(this.width)}px;
          height: ${unsafeCSS(this.height)}px;
        }
        svg#wideSvg:hover:not(:active) {
          cursor: ${this._shapeMoving ? `` : `grab`};
        }
      </style>
      ${this._svgs.map((svg) => html`${svg}`)}
      <movable-shape
        id="test"
        @shape-moved="${this.handleShapeMoved}"
        @shape-dropped="${this.handleShapeDropped}"
        .data="${this._movableShapeData}">
      </movable-shape>
    `;
  }
}

customElements.define("shape-board", ShapeBoard);
